import { commonActions, apiActions } from '../app/actions';
import { boomSeasonForBoard, PLAYOFFS_CODE, SEASON_CODE } from '../lib/constants';
import { makeRequest } from '../lib/fetch';
import { fireMessageEvent } from '../messenger/utils';

const getLeaderBoardRoute = {
  season: 'seasonLeaderboard',
  monthly: 'leaderboard',
  playoffs: 'postSeasonLeaderboard',
};

function leaderboardParams({ board, challengeNumber, season }) {
  const params = { season: season || boomSeasonForBoard(board) };
  if (challengeNumber) {
    params.challengeNumber = challengeNumber;
  }
  return params;
}

const fetchContestRank = ({
  dispatch,
  apiUrl,
  graphqlUrl,
  language,
  userId,
  env,
  currentSession,
  board = null,
  isLocal = false,
  challengeNumber,
  season,
  platform,
}) => {
  const actionType = isLocal ? 'local_leaderboard' : 'leaderboard';
  const { fetchSuccess, fetchFailure } = apiActions(actionType);
  const { fetchFailure: authFailure } = apiActions('auth');
  const suffix = isLocal ? 'Local' : '';
  return makeRequest({
    method: 'GET',
    apiUrl,
    graphqlUrl,
    language,
    platform,
    userId,
    env,
    currentSession,
    route: `${getLeaderBoardRoute[board]}${suffix}`,
    params: leaderboardParams({ board, challengeNumber, season }),
  }).then(
    (resp) => {
      return dispatch(fetchSuccess({ ...resp, board }));
    },
    (err) => {
      if (err.status === 401) {
        return dispatch(authFailure());
      }
      const messageData = {
        err,
        apiUrl,
        route: `${getLeaderBoardRoute[board]}${suffix}`,
        userId,
        currentSession,
      };
      fireMessageEvent({
        type: 'ERROR',
        platform,
        title: 'hockey-challenge-error',
        data: messageData,
      });
      if (err.code === 500) {
        return dispatch(apiActions('data_500').fetchFailure(err));
      }
      return dispatch(fetchFailure(err));
    },
  );
};

export const fetchLeaderboardData = (board, challengeNumber, season) => async (dispatch, getState) => {
  const { config } = getState();
  const { apiUrl, graphqlUrl, language, userId, env, currentSession, platform } = config;
  const { fetchStart } = apiActions('leaderboard');

  dispatch(fetchStart());

  return fetchContestRank({
    dispatch,
    apiUrl,
    graphqlUrl,
    language,
    userId,
    env,
    currentSession,
    board,
    challengeNumber,
    season,
    platform,
  });
};

/**
 * Fetch Local rank status
 * @param {*} board : what leaderboard you are currently on (season/monthly/postSeason)
 * @param {*} challengeNumber : specific to monthly leaderboard specific challenge number
 * @returns array of users in order of their contest rank
 */
export const fetchLocalLeaderboardData = (board, isLocal, challengeNumber, season) => (dispatch, getState) => {
  const { config } = getState();
  const { apiUrl, graphqlUrl, language, userId, env, currentSession, platform } = config;
  const { fetchStart } = apiActions('local_leaderboard');

  dispatch(fetchStart());

  return fetchContestRank({
    dispatch,
    apiUrl,
    graphqlUrl,
    language,
    userId,
    env,
    currentSession,
    board,
    isLocal,
    challengeNumber,
    season,
    platform,
  });
};

/**
 * Fetch season status, sets isCurrentlySeason boolean in store, used to determine whether season is live or not
 * @returns boolean status of season
 */
export const fetchIsCurrentlySeason = () => (dispatch, getState) => {
  const { config } = getState();
  const { apiUrl, graphqlUrl, language, userId, env, currentSession, platform } = config;
  const { fetchStart } = apiActions('season_status');
  const { fetchSuccess, fetchFailure } = apiActions('season_status');
  const { fetchFailure: authFailure } = apiActions('auth');

  dispatch(fetchStart());

  return makeRequest({
    method: 'GET',
    apiUrl,
    graphqlUrl,
    language,
    platform,
    userId,
    env,
    currentSession,
    route: 'seasonLeaderboard',
    params: { season: SEASON_CODE },
  }).then(
    (resp) => {
      const status = resp.status === 'current';
      return dispatch(fetchSuccess(status));
    },
    (err) => {
      if (err.status === 401) {
        return dispatch(authFailure());
      }
      const messageData = {
        err,
        route: 'seasonLeaderboard',
        apiUrl,
        userId,
        currentSession,
      };
      fireMessageEvent({
        type: 'ERROR',
        platform,
        title: 'hockey-challenge-error',
        data: messageData,
      });
      if (err.code === 500) {
        // trigger game crash modal
        return dispatch(apiActions('data_500').fetchFailure(err));
      }
      return dispatch(fetchFailure(err));
    },
  );
};

/**
 * Fetch playoffs status, sets isCurrentlyPlayoffs boolean in store, used to determine whether playoffs are live or not
 * @returns boolean status of playoffs
 */
export const fetchIsCurrentlyPlayoffs = () => (dispatch, getState) => {
  const { config } = getState();
  const { apiUrl, graphqlUrl, language, userId, env, currentSession, platform } = config;
  const { fetchStart } = apiActions('playoffs_status');
  const { fetchSuccess, fetchFailure } = apiActions('playoffs_status');
  const { fetchFailure: authFailure } = apiActions('auth');

  dispatch(fetchStart());

  return makeRequest({
    method: 'GET',
    apiUrl,
    graphqlUrl,
    language,
    platform,
    userId,
    env,
    currentSession,
    route: 'postSeasonLeaderboard',
    params: { season: PLAYOFFS_CODE },
  }).then(
    (resp) => {
      const status = resp.status === 'current';
      return dispatch(fetchSuccess(status));
    },
    (err) => {
      if (err.status === 401) {
        return dispatch(authFailure());
      }
      const messageData = {
        err,
        route: 'postSeasonLeaderboard',
        apiUrl,
        userId,
        currentSession,
      };
      fireMessageEvent({
        type: 'ERROR',
        platform,
        title: 'hockey-challenge-error',
        data: messageData,
      });
      if (err.code === 500) {
        // trigger game crash modal
        return dispatch(apiActions('data_500').fetchFailure(err));
      }
      return dispatch(fetchFailure(err));
    },
  );
};

export const setChallengeNumber = (challengeNumber) => (dispatch) => {
  const { setValue } = commonActions('challenge_number');
  dispatch(setValue(challengeNumber));
};

export const setBoardView = (board) => (dispatch) => {
  const { setValue } = commonActions('board_view');
  dispatch(setValue(board));
};
